import axios from '../helpers/axios';
import qs from 'querystring';
import { alphaSort } from '../helpers';
import {
  IDropdownResponse,
  IOptionSourceListResponse,
  IOptionStatusListResponse,
  IPeopleAgentPost,
  IPeopleAttorneyPost,
  IPeopleBuilds,
  IPeopleClientPost,
  IPeopleCustomsPost,
  IPeopleDetail,
  IPeopleEmployeePost,
  IPeopleLenderPost,
  IPeopleRes,
  IPeopleSupplierPost,
  IPeopleVendorPost
} from '../models';
import { logError } from '../services';
import { useQuery } from 'react-query';

export const getCustomRoles = async (): Promise<IDropdownResponse[]> => {
  try {
    const { data } = await axios.get(`/api/people/custom-roles`);
    return data;
  } catch (error) {
    logError(error, 'getCustomRoles failed');
    return Promise.reject(error);
  }
};

export interface IGetPeopleParams {
  name?: string;
  workplace?: string;
  email?: string;
  personType?: string | number;
  isActive?: boolean;
  customRole?: string;
  page?: number;
  perPage?: number | string;
  sortBy?: string;
  query?: string;
  partnerId?: string | number;
  sortDirection?: string;
  vendorTerritoryId?: string | number;
  sageJobId?: number;
}
export const queryPeopleKey = 'people/lookup';
export const useGetPeopleQuery = (params?: IGetPeopleParams) => {
  return useQuery({
    queryKey: [queryPeopleKey],
    queryFn: () => getPeople(params)
  });
};

export const getPeople = async (params?: IGetPeopleParams): Promise<IPeopleRes> => {
  try {
    const { data } = await axios.get(`/api/people/lookup`, {
      params
    });
    return data;
  } catch (error) {
    logError(error, 'getPeople failed');
    return Promise.reject(error);
  }
};

export const getPerson = async (personId: string): Promise<IPeopleDetail> => {
  try {
    const { data } = await axios.get(`/api/people/${personId}`);
    return data;
  } catch (error) {
    logError(error, 'getPerson failed');
    return Promise.reject(error);
  }
};

export const deletePerson = async (personId: number): Promise<any> => {
  try {
    const { data } = await axios.delete(`/api/people/${personId}`);
    return data;
  } catch (error: any) {
    logError(error, 'deletePerson failed');
    return Promise.reject(error);
  }
};

export const getPeopleTypes = async (): Promise<IDropdownResponse[]> => {
  try {
    const { data } = await axios.get(`/api/people/types`);
    return alphaSort(data, 'description');
  } catch (error) {
    logError(error, 'getPeopleTypes failed');
    return Promise.reject(error);
  }
};

export const getPersonLeadSources = async (): Promise<IOptionSourceListResponse> => {
  try {
    const { data } = await axios.get('/api/client-sources');
    return data;
  } catch (error) {
    logError(error, 'getPersonLeadSources failed');
    return Promise.reject(error);
  }
};

export const getPersonStatuses = async (): Promise<IOptionStatusListResponse> => {
  try {
    const { data } = await axios.get('/api/clients/statuses');
    return data;
  } catch (error) {
    logError(error, 'getPersonStatuses failed');
    return Promise.reject(error);
  }
};

export const resetPerson = async (personId: number): Promise<any> => {
  try {
    const { data } = await axios.post(`/api/people/${personId}/reset`);
    return data;
  } catch (error) {
    logError(error, 'resetPerson failed');
    return Promise.reject(error?.response?.data);
  }
};

export const createPeopleAgent = async (person: IPeopleAgentPost): Promise<any> => {
  try {
    const { data } = await axios.post(`/api/people/agents`, person);
    return data;
  } catch (error) {
    logError({ error, payload: { person } }, 'createPeopleAgent failed');
    return Promise.reject(error?.response?.data);
  }
};

export const updatePeopleAgent = async (
  personId: number,
  person: IPeopleAgentPost
): Promise<any> => {
  try {
    const { data } = await axios.put(`/api/people/agents/${personId}`, person);
    return data;
  } catch (error) {
    logError({ error, payload: { personId, person } }, 'updatePeopleAgent failed');
    return Promise.reject(error?.response?.data);
  }
};

export const createPeopleAttorney = async (person: IPeopleAttorneyPost): Promise<any> => {
  try {
    const { data } = await axios.post(`/api/people/attorneys`, person);
    return data;
  } catch (error) {
    logError({ error, payload: { person } }, 'createPeopleAttorney failed');
    return Promise.reject(error?.response?.data);
  }
};

export const updatePeopleAttorney = async (
  personId: number,
  person: IPeopleAttorneyPost
): Promise<any> => {
  try {
    const { data } = await axios.put(`/api/people/attorneys/${personId}`, person);
    return data;
  } catch (error) {
    logError({ error, payload: { personId, person } }, 'updatePeopleAttorney failed');
    return Promise.reject(error?.response?.data);
  }
};

export const createPeopleClient = async (person: IPeopleClientPost): Promise<any> => {
  try {
    const { data } = await axios.post(`/api/people/clients`, person);
    return data;
  } catch (error) {
    logError({ error, payload: { person } }, 'createPeopleClient failed');
    return Promise.reject(error?.response?.data);
  }
};

export const updatePeopleClient = async (
  personId: number,
  person: IPeopleClientPost
): Promise<any> => {
  try {
    const { data } = await axios.put(`/api/people/clients/${personId}`, person);
    return data;
  } catch (error) {
    logError({ error, payload: { personId, person } }, 'updatePeopleClient failed');
    return Promise.reject(error?.response?.data);
  }
};

export const createPeopleVendor = async (person: IPeopleVendorPost): Promise<any> => {
  try {
    const { data } = await axios.post(`/api/people/vendors`, person);
    return data;
  } catch (error) {
    logError({ error, payload: { person } }, 'createPeopleVendor failed');
    return Promise.reject(error?.response?.data);
  }
};

export const updatePeopleVendor = async (
  personId: number,
  person: IPeopleVendorPost
): Promise<any> => {
  try {
    const { data } = await axios.put(`/api/people/vendors/${personId}`, person);
    return data;
  } catch (error) {
    logError({ error, payload: { personId, person } }, 'updatePeopleVendor failed');
    return Promise.reject(error?.response?.data);
  }
};

export const createPeopleLender = async (person: IPeopleLenderPost): Promise<any> => {
  try {
    const { data } = await axios.post(`/api/people/lenders`, person);
    return data;
  } catch (error) {
    logError({ error, payload: { person } }, 'createPeopleLender failed');
    return Promise.reject(error?.response?.data);
  }
};

export const updatePeopleLender = async (
  personId: number,
  person: IPeopleLenderPost
): Promise<any> => {
  try {
    const { data } = await axios.put(`/api/people/lenders/${personId}`, person);
    return data;
  } catch (error) {
    logError({ error, payload: { personId, person } }, 'updatePeopleLender failed');
    return Promise.reject(error?.response?.data);
  }
};

export const createPeopleSupplier = async (person: IPeopleSupplierPost): Promise<any> => {
  try {
    const { data } = await axios.post(`/api/people/suppliers`, person);
    return data;
  } catch (error) {
    logError({ error, payload: { person } }, 'createPeopleSupplier failed');
    return Promise.reject(error?.response?.data);
  }
};

export const updatePeopleSupplier = async (
  personId: number,
  person: IPeopleSupplierPost
): Promise<any> => {
  try {
    const { data } = await axios.put(`/api/people/suppliers/${personId}`, person);
    return data;
  } catch (error) {
    logError({ error, payload: { personId, person } }, 'updatePeopleSupplier failed');
    return Promise.reject(error?.response?.data);
  }
};

export const createPeopleCustoms = async (person: IPeopleCustomsPost): Promise<any> => {
  try {
    const { data } = await axios.post(`/api/people/customs`, person);
    return data;
  } catch (error) {
    logError({ error, payload: { person } }, 'createPeopleCustoms failed');
    return Promise.reject(error?.response?.data);
  }
};

export const updatePeopleCustoms = async (
  personId: number,
  person: IPeopleCustomsPost
): Promise<any> => {
  try {
    const { data } = await axios.put(`/api/people/customs/${personId}`, person);
    return data;
  } catch (error) {
    logError({ error, payload: { personId, person } }, 'updatePeopleCustoms failed');
    return Promise.reject(error?.response?.data);
  }
};

export const createPeopleEmployee = async (person: IPeopleEmployeePost): Promise<any> => {
  try {
    const { data } = await axios.post(`/api/people/employees`, person);
    return data;
  } catch (error) {
    logError({ error, payload: { person } }, 'createPeopleEmployee failed');
    return Promise.reject(error?.response?.data);
  }
};

export const updatePeopleEmployee = async (
  personId: number,
  person: IPeopleEmployeePost
): Promise<any> => {
  try {
    const { data } = await axios.put(`/api/people/employees/${personId}`, person);
    return data;
  } catch (error) {
    logError({ error, payload: { personId, person } }, 'updatePeopleEmployee failed');
    return Promise.reject(error?.response?.data);
  }
};

export const createPeopleAgentUpload = async (
  personId: number,
  type: string,
  post: FormData
): Promise<any> => {
  try {
    const { data } = await axios.post(`/api/people/agents/${personId}/photos/${type}`, post);
    return data;
  } catch (error) {
    logError({ error, payload: { personId, type, post } }, 'createPeopleAgentUpload failed');
    return Promise.reject(error?.response?.data);
  }
};

export const getPeopleAgentImage = async (personId: number, type: string): Promise<string> => {
  try {
    const { data } = await axios.get(`/api/people/agents/${personId}/photos/${type}`);
    return data;
  } catch (error) {
    logError({ error, payload: { personId, type } }, 'getPeopleAgentImages failed');
    return Promise.reject(error?.response?.data);
  }
};

export const createPeopleLenderUpload = async (
  personId: number,
  type: string,
  post: FormData
): Promise<any> => {
  try {
    const { data } = await axios.post(`/api/people/lenders/${personId}/photos/${type}`, post);
    return data;
  } catch (error) {
    logError({ error, payload: { personId, type, post } }, 'createPeopleLenderUpload failed');
    return Promise.reject(error?.response?.data);
  }
};

export const getPeopleLenderImage = async (personId: number, type: string): Promise<string> => {
  try {
    const { data } = await axios.get(`/api/people/lenders/${personId}/photos/${type}`);
    return data;
  } catch (error) {
    logError({ error, payload: { personId, type } }, 'getPeopleLenderImages failed');
    return Promise.reject(error?.response?.data);
  }
};

export const putPeopleActivation = async (
  personId: number | string,
  isActive: boolean
): Promise<boolean> => {
  try {
    const { data } = await axios.put(`/api/people/${personId}/is-active`, isActive, {
      headers: { 'Content-Type': 'application/json' } // requires this, otherwise throws a 415 error
    });
    return data;
  } catch (error) {
    logError({ error, payload: { personId, isActive } }, 'putPeopleActivation failed');
    return Promise.reject(error?.response?.data);
  }
};

export const getPeopleBuilds = async (personId: number): Promise<IPeopleBuilds[]> => {
  try {
    const { data } = await axios.get(`/api/people/${personId}/builds`);
    return data;
  } catch (error) {
    logError({ error, payload: { personId } }, 'getPeopleBuilds failed');
    return Promise.reject(error?.response?.data);
  }
};

export const getLenderBuilds = async (lenderPersonId: number): Promise<IPeopleBuilds[]> => {
  try {
    const { data } = await axios.get(`/api/people/${lenderPersonId}/lender-builds`);
    return data;
  } catch (error) {
    logError({ error, payload: { lenderPersonId } }, 'getLenderBuilds failed');
    return Promise.reject(error?.response?.data);
  }
};

export const deleteVendorUser = async (personId: number, vendorId: number): Promise<any> => {
  try {
    const { data } = await axios.delete(`/api/people/vendors/${personId}/${vendorId}`);
    return data;
  } catch (error: any) {
    logError(error, 'deleteVendorUser failed');
    return Promise.reject(error);
  }
};

export const createPeopleLenderProcessor = async (person: IPeopleLenderPost): Promise<any> => {
  try {
    const { data } = await axios.post(`/api/people/lender-processors`, person);
    return data;
  } catch (error) {
    logError({ error, payload: { person } }, 'createPeopleLenderProcessor failed');
    return Promise.reject(error?.response?.data);
  }
};

export const updatePeopleLenderProcessor = async (
  personId: number,
  person: IPeopleLenderPost
): Promise<any> => {
  try {
    const { data } = await axios.put(`/api/people/lender-processors/${personId}`, person);
    return data;
  } catch (error) {
    logError({ error, payload: { personId, person } }, 'updatePeopleLenderProcessor failed');
    return Promise.reject(error?.response?.data);
  }
};

export const getPeopleLenderTerritories = async (LenderPersonIds: number[]): Promise<any> => {
  try {
    const { data } = await axios.get(`/api/people/lender-territories`, {
      params: { LenderPersonIds },
      paramsSerializer: params => {
        return qs.stringify(params);
      }
    });
    return data;
  } catch (error) {
    logError({ error, payload: { LenderPersonIds } }, 'getPeopleLenderTerritories failed');
    return Promise.reject(error?.response?.data);
  }
};
